import type { HttpToolConfig, ToolDto } from '@agent-platform/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useState } from 'react';
import { toast } from 'sonner';
import { ErrorAlert } from '@/components/error-alert';
import { changedFields, FieldErrors, useToolCapabilities } from '@/components/resource-form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';

const DEFAULT_SCHEMA = JSON.stringify(
  {
    type: 'object',
    properties: { city: { type: 'string', description: 'City name' } },
    required: ['city'],
  },
  null,
  2,
);

const json = (value: unknown) => JSON.stringify(value, null, 2);

/** Why a tool can only be viewed here, or null when it can be edited. */
export function toolReadOnlyReason(tool: ToolDto): string | null {
  if (tool.pluginId) return 'Installed by a plugin. Change it by updating the plugin.';
  if (tool.kind === 'sandbox')
    return 'Created from a sandbox: it runs the model’s code there, so there’s nothing to configure.';
  return null;
}

/**
 * Creates a tool (no `tool`) or edits one. Name and kind are fixed once created; `readOnly` shows
 * the tool without saving (plugin and sandbox tools, viewers).
 */
export function ToolFormDialog({
  tenantId,
  tool,
  readOnly = false,
  open,
  onOpenChange,
  trigger,
}: {
  tenantId: string;
  tool: ToolDto | null;
  readOnly?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        {/* Content unmounts when closed, so the form starts from the tool each time it opens. */}
        <ToolForm
          tenantId={tenantId}
          tool={tool}
          readOnly={readOnly}
          onDone={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function ToolForm({
  tenantId,
  tool,
  readOnly,
  onDone,
}: {
  tenantId: string;
  tool: ToolDto | null;
  readOnly: boolean;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const caps = useToolCapabilities(tenantId);
  const editing = tool !== null;
  const http = tool && 'method' in tool.config ? tool.config : null;
  const cli = tool && 'command' in tool.config ? tool.config : null;

  const [kind, setKind] = useState<'http' | 'cli'>(tool?.kind === 'cli' ? 'cli' : 'http');
  const [name, setName] = useState(tool?.name ?? '');
  const [description, setDescription] = useState(tool?.description ?? '');
  const [schema, setSchema] = useState(tool ? json(tool.inputSchema) : DEFAULT_SCHEMA);
  const [method, setMethod] = useState<string>(http?.method ?? 'GET');
  const [url, setUrl] = useState(
    http?.urlTemplate ?? (editing ? '' : 'https://api.example.com/weather?city={{input.city}}'),
  );
  const [headers, setHeaders] = useState(
    http
      ? Object.keys(http.headers).length > 0
        ? json(http.headers)
        : ''
      : '{\n  "Authorization": "Bearer {{env.WEATHER_TOKEN}}"\n}',
  );
  const [body, setBody] = useState(http?.bodyTemplate ?? '');
  const [command, setCommand] = useState(cli?.command ?? '');
  const [args, setArgs] = useState(cli ? cli.argsTemplate.join('\n') : '{{input.city}}');
  const [localError, setLocalError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      tool
        ? api<ToolDto>(`/tools/${tool.id}`, { method: 'PATCH', body: payload })
        : api<ToolDto>('/tools', { method: 'POST', body: payload }),
    onSuccess: (saved) => {
      toast.success(editing ? `Tool ${saved.name} saved` : `Tool ${saved.name} created`);
      void queryClient.invalidateQueries({ queryKey: ['tools', tenantId] });
      onDone();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    let inputSchema: unknown;
    // Partial in create mode: the server fills the limits the form doesn't show.
    let config: Record<string, unknown>;
    try {
      inputSchema = JSON.parse(schema);
      config =
        kind === 'http'
          ? {
              method: method as HttpToolConfig['method'],
              urlTemplate: url,
              headers: headers.trim() ? (JSON.parse(headers) as HttpToolConfig['headers']) : {},
              ...(body.trim() ? { bodyTemplate: body } : {}),
              // Limits the form doesn't show keep their current values.
              ...(http
                ? { timeoutMs: http.timeoutMs, responseMaxBytes: http.responseMaxBytes }
                : {}),
            }
          : {
              command,
              argsTemplate: args.split('\n').filter((a) => a.length > 0),
              ...(cli ? { timeoutMs: cli.timeoutMs, outputMaxBytes: cli.outputMaxBytes } : {}),
            };
    } catch {
      setLocalError('The input schema and headers must be valid JSON.');
      return;
    }
    setLocalError(null);
    if (!tool) {
      save.mutate({ name, description, kind, inputSchema, config });
      return;
    }
    const changes = changedFields(
      { description, inputSchema, config },
      { description: tool.description, inputSchema: tool.inputSchema, config: tool.config },
    );
    if (Object.keys(changes).length === 0) {
      onDone();
      return;
    }
    save.mutate(changes);
  };

  const cliAllowed = (caps.data?.cliToolAllowlist.length ?? 0) > 0;
  const commands = [
    ...new Set([...(caps.data?.cliToolAllowlist ?? []), ...(cli ? [cli.command] : [])]),
  ];
  const readOnlyReason = tool && readOnly ? toolReadOnlyReason(tool) : null;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{!tool ? 'Create a tool' : readOnly ? 'View tool' : 'Edit tool'}</DialogTitle>
        <DialogDescription>
          {readOnlyReason ?? (
            <>
              Templates can use <code>{'{{input.x}}'}</code> (from the model),{' '}
              <code>{'{{vars.x}}'}</code> (from an endpoint’s request mapping) and{' '}
              <code>{'{{env.X}}'}</code> (environment variables).
            </>
          )}
        </DialogDescription>
      </DialogHeader>
      <form className="grid gap-4" onSubmit={submit} noValidate>
        <ErrorAlert error={save.error} />
        <FieldErrors error={save.error} />
        <fieldset disabled={readOnly} className="grid min-w-0 gap-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="tool-name">Name</Label>
              <Input
                id="tool-name"
                className="font-mono"
                placeholder="get_weather"
                value={name}
                readOnly={editing}
                aria-describedby={editing ? 'tool-name-help' : undefined}
                onChange={(e) => setName(e.target.value)}
              />
              {editing && !readOnly && (
                <p id="tool-name-help" className="text-muted-foreground text-xs">
                  A tool’s name can’t be changed.
                </p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="tool-kind">Kind</Label>
              {tool?.kind === 'sandbox' ? (
                <Input id="tool-kind" value="Sandbox code" readOnly />
              ) : (
                <Select
                  value={kind}
                  onValueChange={(v) => setKind(v as 'http' | 'cli')}
                  disabled={editing || readOnly}
                >
                  <SelectTrigger id="tool-kind" aria-label="Kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="http">HTTP request</SelectItem>
                    <SelectItem value="cli" disabled={!cliAllowed && kind !== 'cli'}>
                      Command line
                      {cliAllowed ? '' : ' (not enabled on this server)'}
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="tool-description">Description (the model reads this)</Label>
            <Input
              id="tool-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Current weather for a city"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="tool-schema">Input schema (JSON Schema)</Label>
            <Textarea
              id="tool-schema"
              className="min-h-32 font-mono text-xs"
              value={schema}
              onChange={(e) => setSchema(e.target.value)}
            />
          </div>
          {tool?.kind === 'sandbox' ? null : kind === 'http' ? (
            <>
              <div className="grid gap-4 md:grid-cols-[8rem_1fr]">
                <div className="grid gap-1.5">
                  <Label htmlFor="tool-method">Method</Label>
                  <Select value={method} onValueChange={setMethod} disabled={readOnly}>
                    <SelectTrigger id="tool-method" aria-label="Method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="tool-url">URL template</Label>
                  <Input
                    id="tool-url"
                    className="font-mono text-xs"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="tool-headers">Headers (JSON)</Label>
                <Textarea
                  id="tool-headers"
                  className="font-mono text-xs"
                  value={headers}
                  onChange={(e) => setHeaders(e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="tool-body">Body template (optional, JSON)</Label>
                <Textarea
                  id="tool-body"
                  className="font-mono text-xs"
                  placeholder={'{"city": "{{input.city}}"}'}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              </div>
            </>
          ) : (
            <>
              <div className="grid gap-1.5">
                <Label htmlFor="tool-command">Command</Label>
                <Select value={command} onValueChange={setCommand} disabled={readOnly}>
                  <SelectTrigger id="tool-command" aria-label="Command">
                    <SelectValue placeholder="Choose an allowed command" />
                  </SelectTrigger>
                  <SelectContent>
                    {commands.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="tool-args">Arguments (one per line, no shell)</Label>
                <Textarea
                  id="tool-args"
                  className="font-mono text-xs"
                  value={args}
                  onChange={(e) => setArgs(e.target.value)}
                />
              </div>
            </>
          )}
        </fieldset>
        {localError && <p className="text-destructive text-sm">{localError}</p>}
        <DialogFooter>
          {readOnly ? (
            <Button type="button" variant="outline" onClick={onDone}>
              Close
            </Button>
          ) : (
            <Button type="submit" disabled={save.isPending}>
              {editing ? 'Save changes' : 'Create tool'}
            </Button>
          )}
        </DialogFooter>
      </form>
    </>
  );
}
